import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from './api';
import type { AdminTransferStyle } from './types';
import TransferStyleTestModal from './components/TransferStyleTestModal';
import TransferStylePromptDesignerModal from './components/TransferStylePromptDesignerModal';

// ── Utilities ─────────────────────────────────────────────────────────────────

const emptyStyle = (): AdminTransferStyle => ({
  id: '', name: '', description: '', thumbnailGcsPath: '', prompt: '', order: 999,
});

type View =
  | { type: 'list' }
  | { type: 'editor'; mode: 'create' | 'edit'; initial: AdminTransferStyle };

// ── 編輯器 ─────────────────────────────────────────────────────────────────────

const TransferStyleEditor: React.FC<{
  mode: 'create' | 'edit';
  initial: AdminTransferStyle;
  onDone: () => void;
  onCancel: () => void;
}> = ({ mode, initial, onDone, onCancel }) => {
  const [form, setForm] = useState<AdminTransferStyle>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDesignerModal, setShowDesignerModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = <K extends keyof AdminTransferStyle>(key: K, value: AdminTransferStyle[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      const { url } = await adminApi.uploadToolkitImage(file);
      update('thumbnailGcsPath', url);
    } catch (e: any) {
      setError(e.message || '上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    // 基本驗證
    if (!form.id.trim()) return setError('請填寫 ID');
    if (!form.name.trim()) return setError('請填寫名稱');
    if (!form.description.trim()) return setError('請填寫描述');
    if (!form.prompt.trim()) return setError('請填寫風格 Prompt');

    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {
        await adminApi.createTransferStyle(form);
      } else {
        await adminApi.updateTransferStyle(initial.id, form);
      }
      onDone();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 標頭 */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onCancel} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-1">
            ← 返回列表
          </button>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {mode === 'create' ? '新增風格' : `編輯風格：${initial.name || initial.id}`}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="text-sm border dark:border-gray-600 text-gray-600 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? '儲存中...' : '儲存'}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded">{error}</p>
      )}

      {/* 表單卡片 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-5">
        {/* ID */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            ID <span className="text-red-500">*</span>
            <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">（建立後不建議更改，會影響使用此 ID 的呼叫）</span>
          </label>
          <input
            type="text"
            value={form.id}
            onChange={e => update('id', e.target.value)}
            disabled={mode === 'edit'}
            placeholder="例如：figure"
            className="w-full text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed font-mono"
          />
        </div>

        {/* 名稱 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            名稱 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="例如：公仔風格"
            className="w-full text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 描述 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            描述 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="例如：超寫實風格公仔"
            className="w-full text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 顯示順序 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            顯示順序
            <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">（數字越小越前面，可在列表用 ↑↓ 快速調整）</span>
          </label>
          <input
            type="number"
            value={form.order ?? 0}
            onChange={e => update('order', Number(e.target.value) || 0)}
            className="w-32 text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* 縮圖 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            縮圖
          </label>
          <div className="flex items-start gap-4">
            <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900 border dark:border-gray-700 flex-shrink-0 flex items-center justify-center">
              {form.thumbnailGcsPath ? (
                <img
                  src={form.thumbnailGcsPath}
                  alt="縮圖預覽"
                  className="w-full h-full object-cover"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span className="text-xs text-gray-400 dark:text-gray-600">無縮圖</span>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = '';
                }}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {uploading ? '上傳中...' : '上傳縮圖'}
                </button>
                {form.thumbnailGcsPath && (
                  <button
                    onClick={() => update('thumbnailGcsPath', '')}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    移除
                  </button>
                )}
              </div>
              <input
                type="text"
                value={form.thumbnailGcsPath}
                onChange={e => update('thumbnailGcsPath', e.target.value)}
                placeholder="或直接貼上圖片網址"
                className="w-full text-xs border dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Prompt */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
              風格 Prompt <span className="text-red-500">*</span>
              <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">（傳給 Gemini 的完整風格指令）</span>
            </label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowDesignerModal(true)}
                className="text-xs px-2.5 py-1 rounded border border-emerald-300 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
              >
                🪄 AI 協助設計
              </button>
              <button
                type="button"
                onClick={() => setShowTestModal(true)}
                disabled={!form.prompt.trim()}
                title={!form.prompt.trim() ? '請先輸入 Prompt' : '用當前 Prompt 測試生成'}
                className="text-xs px-2.5 py-1 rounded border border-blue-300 dark:border-blue-700/60 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ▶ 測試這段 Prompt
              </button>
            </div>
          </div>
          <textarea
            value={form.prompt}
            onChange={e => update('prompt', e.target.value)}
            rows={14}
            placeholder="請輸入詳細的風格描述..."
            className="w-full text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono leading-relaxed resize-y"
          />
        </div>
      </div>

      {showTestModal && (
        <TransferStyleTestModal
          prompt={form.prompt}
          styleName={form.name}
          onClose={() => setShowTestModal(false)}
          onSaveAsThumbnail={url => update('thumbnailGcsPath', url)}
        />
      )}
      {showDesignerModal && (
        <TransferStylePromptDesignerModal
          currentPrompt={form.prompt}
          onClose={() => setShowDesignerModal(false)}
          onApply={(mode, generated) => {
            if (mode === 'replace') update('prompt', generated);
            else update('prompt', form.prompt.trim() ? `${form.prompt.trimEnd()}\n\n${generated}` : generated);
          }}
        />
      )}
    </div>
  );
};

// ── 列表 ───────────────────────────────────────────────────────────────────────

const TransferStyleList: React.FC<{
  styles: AdminTransferStyle[];
  loading: boolean;
  error: string;
  onCreate: () => void;
  onEdit: (item: AdminTransferStyle) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  reorderingId: string | null;
}> = ({ styles, loading, error, onCreate, onEdit, onDelete, onMove, reorderingId }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">風格轉換管理</h1>
        <button
          onClick={onCreate}
          className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          + 新增風格
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">載入中...</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                <th className="w-24 text-center px-2 py-3 font-medium">順序</th>
                <th className="w-20 text-left px-4 py-3 font-medium">縮圖</th>
                <th className="text-left px-4 py-3 font-medium">ID</th>
                <th className="text-left px-4 py-3 font-medium">名稱</th>
                <th className="text-left px-4 py-3 font-medium">描述</th>
                <th className="text-right px-4 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {styles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                    尚無風格資料
                  </td>
                </tr>
              ) : styles.map((s, idx) => (
                <tr
                  key={s.id}
                  className="border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <td className="px-2 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onMove(s.id, 'up')}
                        disabled={idx === 0 || reorderingId !== null}
                        title="上移"
                        className="w-6 h-6 rounded text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >▲</button>
                      <span className="text-xs font-mono text-gray-400 dark:text-gray-500 w-6 text-center">
                        {s.order ?? 0}
                      </span>
                      <button
                        onClick={() => onMove(s.id, 'down')}
                        disabled={idx === styles.length - 1 || reorderingId !== null}
                        title="下移"
                        className="w-6 h-6 rounded text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >▼</button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-14 h-14 rounded overflow-hidden bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                      {s.thumbnailGcsPath ? (
                        <img
                          src={s.thumbnailGcsPath}
                          alt={s.name}
                          className="w-full h-full object-cover"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">無圖</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300 max-w-[8rem] truncate">{s.id}</td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-md truncate">{s.description}</td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => onEdit(s)} className="text-xs text-blue-500 hover:text-blue-700 transition-colors">
                      編輯
                    </button>
                    <button onClick={() => onDelete(s.id)} className="text-xs text-red-400 hover:text-red-600 transition-colors">
                      刪除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── TransferStyleManagement ────────────────────────────────────────────────────

const TransferStyleManagement: React.FC = () => {
  const [styles, setStyles] = useState<AdminTransferStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>({ type: 'list' });
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getTransferStyles();
      setStyles(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!confirm(`確定要刪除風格「${id}」？`)) return;
    try {
      await adminApi.deleteTransferStyle(id);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDone = () => {
    setView({ type: 'list' });
    load();
  };

  // 將兩個相鄰項目交換顯示順序；若所有項目 order 都相同，先依當前列表索引正規化再交換
  const handleMove = async (id: string, direction: 'up' | 'down') => {
    const idx = styles.findIndex(s => s.id === id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= styles.length) return;

    setReorderingId(id);
    try {
      // 以當前顯示順序作為基準（後端已 sort），把所有項目正規化為 0..N-1
      const normalized = styles.map((s, i) => ({ ...s, order: i }));
      // 交換目標兩項
      [normalized[idx].order, normalized[targetIdx].order] = [normalized[targetIdx].order, normalized[idx].order];
      // 找出 order 實際變動的項目並 PUT
      const changed = normalized.filter(n => {
        const original = styles.find(s => s.id === n.id);
        return (original?.order ?? 0) !== n.order;
      });
      await Promise.all(changed.map(n => adminApi.updateTransferStyle(n.id, n)));
      await load();
    } catch (e: any) {
      alert(e.message || '排序失敗');
    } finally {
      setReorderingId(null);
    }
  };

  if (view.type === 'editor') {
    return (
      <TransferStyleEditor
        mode={view.mode}
        initial={view.initial}
        onDone={handleDone}
        onCancel={() => setView({ type: 'list' })}
      />
    );
  }

  return (
    <TransferStyleList
      styles={styles}
      loading={loading}
      error={error}
      onCreate={() => setView({ type: 'editor', mode: 'create', initial: emptyStyle() })}
      onEdit={item => setView({ type: 'editor', mode: 'edit', initial: item })}
      onDelete={handleDelete}
      onMove={handleMove}
      reorderingId={reorderingId}
    />
  );
};

export default TransferStyleManagement;
